import React, { useState, useEffect } from 'react';
import Switch from 'react-switch';
import { validateForm } from '../../utils/formUtils';
import { toastSuccess, toastError, confirmDelete } from '../../utils/alertHelper';
import axios from '../../utils/axiosInstance';
import { FaEdit, FaTrash } from 'react-icons/fa';

const SliderPage = () => {
  const [banners, setBanners] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedBanner, setSelectedBanner] = useState(null);
  const [formValues, setFormValues] = useState({
    images: null,
    link: '',
    active: false,
    startDate: '',
    endDate: '',
    price: '',
  });
  const [editingId, setEditingId] = useState(null);
  const [errors, setErrors] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [imagePreview, setImagePreview] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // Stats & date range filter
  const [dateRange, setDateRange] = useState({ start: '', end: '' });
  const [filteredStats, setFilteredStats] = useState({ count: 0, totalPrice: 0 });
  const [showFilterModal, setShowFilterModal] = useState(false); // Add filter modal state
  const [tempDateRange, setTempDateRange] = useState({ start: '', end: '' }); // Temp for modal

  const limit = parseInt(process.env.REACT_APP_PAGE_LIMIT || 10);

  // Init bootstrap tooltips
  useEffect(() => {
    if (window.bootstrap && window.bootstrap.Tooltip) {
      const tooltipTriggerList = document.querySelectorAll('[data-bs-toggle="tooltip"]');
      tooltipTriggerList.forEach(tooltipTriggerEl => {
        try { new window.bootstrap.Tooltip(tooltipTriggerEl, { html: true, sanitize: false }); } catch (e) { }
      });
    }
  }, [banners, showModal, showViewModal]);

  const validateImageFile = (file, requiredWidth, requiredHeight) => {
    return new Promise((resolve, reject) => {
      if (!file) return reject('No file provided');
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png'];
      const maxSize = 2 * 1024 * 1024; // 2MB
      if (!allowedTypes.includes(file.type)) return reject('Only JPG and PNG images are allowed');
      if (file.size > maxSize) return reject('Image size should be less than 2MB');
      const img = new Image();
      img.onload = () => {
        if (requiredWidth && requiredHeight) {
          if (img.width !== requiredWidth || img.height !== requiredHeight) {
            reject(`Image dimensions must be ${requiredWidth}x${requiredHeight}px`);
          } else {
            resolve(true);
          }
        } else resolve(true);
      };
      img.onerror = () => reject('Invalid image file');
      img.src = URL.createObjectURL(file);
    });
  };

  useEffect(() => {
    fetchBanners(1, searchQuery); // Always fetch all on search change
    // eslint-disable-next-line
  }, [searchQuery]);

  useEffect(() => {
    computeStats(banners, dateRange);
    // eslint-disable-next-line
  }, [banners, dateRange]);

  const fetchBanners = async (page = 1, search = '') => {
    try {
      setLoading(true);
      const url = `/admin/api/sliders?page=${page}&limit=${limit}&search=${encodeURIComponent(search)}`;
      const res = await axios.get(url);
      let bannersData = [];
      let totalCount = 0;
      let totalPrice = 0;
      if (res.data.sliders) {
        bannersData = res.data.sliders;
        totalCount = res.data.totalCount || res.data.sliders.length;
        totalPrice = res.data.totalPrice || 0;
      } else if (res.data.slider) {
        bannersData = [res.data.slider];
        totalCount = 1;
        totalPrice = bannersData[0]?.price || 0;
      } else if (Array.isArray(res.data)) {
        bannersData = res.data;
        totalCount = res.data.length;
        totalPrice = bannersData.reduce((sum, b) => sum + (Number(b.price) || 0), 0);
      } else {
        bannersData = [];
        totalCount = 0;
        totalPrice = 0;
      }
      setBanners(bannersData);
      setTotalPages(Math.ceil((totalCount || bannersData.length) / limit) || 1);
      if (!dateRange.start && !dateRange.end) {
        setFilteredStats({ count: totalCount, totalPrice });
      } else {
        computeStats(bannersData, dateRange);
      }
    } catch (err) {
      toastError('Failed to fetch Sakhyam Ads');
    } finally {
      setLoading(false);
    }
  };

  const computeStats = (bannerList = banners, range = dateRange) => {
    let filtered = bannerList;
    if (range.start && range.end) {
      const start = new Date(range.start);
      const end = new Date(range.end);
      filtered = bannerList.filter(b =>
        b.startDate && b.endDate &&
        new Date(b.startDate) >= start &&
        new Date(b.endDate) <= end
      );
    }
    const count = filtered.length;
    const totalPrice = filtered.reduce((sum, b) =>
      sum + (Number(b.price) || 0), 0
    );
    setFilteredStats({ count, totalPrice });
  };

  const handleSave = async () => {
    const validationErrors = validateForm(formValues, {
      images: { required: !editingId },
      link: { required: true },
      startDate: { required: true },
      endDate: { required: true },
      price: { required: true },
    });
    if (formValues.images instanceof File) {
      const maxSize = 2 * 1024 * 1024; // 2MB
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png'];
      if (formValues.images.size > maxSize) {
        validationErrors.images = 'Image size should be less than 2MB';
      }
      if (!allowedTypes.includes(formValues.images.type)) {
        validationErrors.images = 'Only JPG and PNG images are allowed';
      }
    }
    if (formValues.startDate && formValues.endDate) {
      const startDate = new Date(formValues.startDate);
      const endDate = new Date(formValues.endDate);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      // Only enforce "no past start date" for new records. Allow editing existing records to have past dates.
      if (!editingId && startDate < today) {
        validationErrors.startDate = 'Start date cannot be in the past';
      }
      if (endDate <= startDate) {
        validationErrors.endDate = 'End date must be after start date';
      }
    }
    if (formValues.price && isNaN(Number(formValues.price))) {
      validationErrors.price = 'Price must be a number';
    }
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }
    try {
      setSaving(true);
      const formData = new FormData();
      if (formValues.images instanceof File) {
        formData.append('images', formValues.images);
      }
      formData.append('active', formValues.active);
      formData.append('link', formValues.link || '');
      formData.append('startDate', formValues.startDate || '');
      formData.append('endDate', formValues.endDate || '');
      formData.append('price', formValues.price || '');
      const config = { headers: { 'Content-Type': 'multipart/form-data' } };
      let response;
      if (editingId) {
        response = await axios.put(`/admin/api/sliders/${editingId}`, formData, config);
        toastSuccess('Sakhyam Ads updated successfully!');
      } else {
        response = await axios.post('/admin/api/sliders', formData, config);
        toastSuccess('Sakhyam Ads added successfully!');
      }
      setCurrentPage(1);
      fetchBanners(1, searchQuery);
      handleCancel();
    } catch (err) {
      toastError(err.response?.data?.message || 'Failed to save Sakhyam Ads');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (banner) => {
    setFormValues({
      images: null,
      link: banner.link || '',
      active: banner.active || false,
      startDate: banner.startDate ? banner.startDate.split('T')[0] : '',
      endDate: banner.endDate ? banner.endDate.split('T')[0] : '',
      price: banner.price || '',
    });
    setImagePreview(banner.images || '');
    setEditingId(banner._id);
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    const result = await confirmDelete('Are you sure you want to delete this Sakhyam Ads?');
    if (result?.isConfirmed) {
      try {
        await axios.delete(`/admin/api/sliders/${id}`);
        toastSuccess('Sakhyam Ads deleted successfully!');
        setCurrentPage(1);
        fetchBanners(1, searchQuery);
      } catch (err) {
        toastError('Failed to delete Sakhyam Ads');
      }
    }
  };

  const handleCancel = () => {
    setShowModal(false);
    setFormValues({
      images: null,
      link: '',
      active: false,
      startDate: '',
      endDate: '',
      price: '',
    });
    setImagePreview('');
    setEditingId(null);
    setErrors({});
  };

  const handleToggleStatus = async (id, currentStatus) => {
    try {
      const now = new Date().toISOString();
      const payload = {
        active: !currentStatus,
        updatedAt: now,
        activatedAt: !currentStatus ? now : undefined,
        deactivatedAt: currentStatus ? now : undefined,
      };
      const config = { headers: { 'Content-Type': 'application/json' } };
      await axios.put(`/admin/api/sliders/${id}`, payload, config);
      toastSuccess(`Sakhyam Ads ${!currentStatus ? 'activated' : 'deactivated'} successfully!`);
      fetchBanners(currentPage, searchQuery);
    } catch (err) {
      toastError(err.response?.data?.message || 'Failed to update sakhyam ads status');
    }
  };

  const handleViewToggle = async () => {
    if (!selectedBanner) return;
    try {
      await handleToggleStatus(selectedBanner._id, selectedBanner.active);
      setSelectedBanner(prev => prev ? { ...prev, active: !prev.active, updatedAt: new Date().toISOString() } : prev);
      fetchBanners(currentPage, searchQuery);
    } catch (err) {
      // handled in handleToggleStatus
    }
  };

  // Date Range Filter Handlers
  const handleDateFilter = () => {
    setTempDateRange(dateRange); // Open modal with current range
    setShowFilterModal(true);
  };
  const handleApplyFilter = () => {
    setDateRange(tempDateRange);
    computeStats(banners, tempDateRange);
    setShowFilterModal(false);
  };
  const handleDateReset = () => {
    setDateRange({ start: '', end: '' });
    setTempDateRange({ start: '', end: '' });
    computeStats(banners, { start: '', end: '' });
    setShowFilterModal(false);
  };

  return (
    <div className="container mt-2">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h3 className="mb-0">Sakhyam Ads</h3>
      </div>

      {/* Stats and Filter Row */}
      <div className="d-flex justify-content-between align-items-end mb-3 flex-wrap">
        {/* Stats Section */}
        <div className="d-flex align-items-center gap-3 flex-wrap">
          <div>
            <span className="fw-bold">Sliders count:</span>
            <span className="ms-1">{filteredStats.count}</span>
          </div>
          <div>
            <span className="fw-bold">Total price:</span>
            <span className="ms-1">₹{filteredStats.totalPrice}</span>
          </div>
          {dateRange.start && dateRange.end && (
            <span className="text-muted ms-2" style={{ fontSize: '13px' }}>
              (filtered by date range)
            </span>
          )}
        </div>
        {/* Filter Section */}
        <div className="d-flex align-items-end gap-2 flex-wrap">
          <div>
            <label className="form-label mb-0" style={{ fontSize: '13px' }}>Start Date</label>
            <input
              type="date"
              className="form-control form-control-sm"
              value={dateRange.start}
              onChange={e => setDateRange(prev => ({ ...prev, start: e.target.value }))}
              style={{ minWidth: 130 }}
            />
          </div>
          <div>
            <label className="form-label mb-0" style={{ fontSize: '13px' }}>End Date</label>
            <input
              type="date"
              className="form-control form-control-sm"
              value={dateRange.end}
              onChange={e => setDateRange(prev => ({ ...prev, end: e.target.value }))}
              style={{ minWidth: 130 }}
            />
          </div>
          <button
            className="btn btn-sm btn-success"
            onClick={handleDateFilter}
            style={{ minWidth: 80 }}
          >
            Filter
          </button>
          <button
            className="btn btn-sm btn-secondary"
            onClick={handleDateReset}
            style={{ minWidth: 80 }}
          >
            Reset
          </button>
        </div>
      </div>

      <div className="card shadow-sm">
        <div className="card-header d-flex justify-content-between align-items-center p-2">
          <h5 className="mb-0">Sakhyam Ads List</h5>
          <button
            className="btn btn-sm"
            style={{ backgroundColor: '#F94F01', color: '#fff' }}
            onClick={() => {
              setFormValues({
                images: null,
                link: '',
                active: false,
                startDate: '',
                endDate: '',
                price: '',
              });
              setImagePreview('');
              setEditingId(null);
              setErrors({});
              setShowModal(true);
            }}
          >
            + Add New Slider
          </button>
        </div>
        <div className="table-responsive">
          <div className="card-body p-0">
            <table
              className="table table-bordered table-sm mb-0"
              style={{ fontSize: '14px', lineHeight: '2' }}
            >
              <thead className="table-light">
                <tr>
                  <th>Sr no.</th>
                  <th>Ads Id</th>
                  <th>Image</th>
                  <th>Link</th>
                  <th>Start Date</th>
                  <th>End Date</th>
                  <th>Price</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="9" className="text-center py-3">
                      <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                    </td>
                  </tr>
                ) : banners.length > 0 ? (
                  banners.map((banner, idx) => (
                    <tr key={banner._id}>
                      <td>{idx + 1}</td>
                      <td>{banner.bannerId || banner._id}</td>
                      <td>
                              {banner.images && banner.images.trim() !== '' ? (
                                <img
                                  src={banner.images}
                                  alt="Slider"
                                  style={{
                                    width: '60px',
                                    height: '40px',
                                    objectFit: 'cover',
                                    borderRadius: '4px',
                                    cursor: 'pointer'
                                  }}
                                  onClick={() => window.open(banner.images, '_blank')}
                                  data-bs-toggle="tooltip"
                                  data-bs-placement="top"
                                  data-bs-html="true"
                                  title={`<div style="text-align:center;"><img src="${banner.images}" style="max-width:220px;max-height:140px;display:block;margin-bottom:6px;border-radius:4px;"/> <div style="font-size:12px;color:#666">${banner.createdAt ? 'Added: ' + new Date(banner.createdAt).toLocaleString() : 'Added: -'}<br/>${banner.updatedAt ? 'Edited: ' + new Date(banner.updatedAt).toLocaleString() : 'Edited: -'}</div></div>`}
                                />
                              ) : (
                                <span className="text-muted">No Image</span>
                              )}
                            </td>
                      <td>
                        {banner.link ? (
                          <a
                            href={banner.link}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-primary text-decoration-none"
                            style={{ fontSize: '12px' }}
                            title={banner.link}
                          >
                            {banner.link.length > 30 ? banner.link.substring(0, 30) + '...' : banner.link}
                          </a>
                        ) : (
                          <span className="text-muted">No Link</span>
                        )}
                      </td>
                      <td>
                        {banner.startDate ? (
                          <span style={{ fontSize: '12px' }}>
                            {new Date(banner.startDate).toLocaleDateString()}
                          </span>
                        ) : (
                          <span className="text-muted">-</span>
                        )}
                      </td>
                      <td>
                        {banner.endDate ? (
                          <span style={{ fontSize: '12px' }}>
                            {new Date(banner.endDate).toLocaleDateString()}
                          </span>
                        ) : (
                          <span className="text-muted">-</span>
                        )}
                      </td>
                      <td>
                        {banner.price !== undefined && banner.price !== null && banner.price !== ''
                          ? banner.price
                          : 0}
                      </td>
                      <td>
                        <button
                          type="button"
                          className={`badge ${banner.active ? 'badge-success' : 'badge-secondary'}`}
                          style={{
                            backgroundColor: banner.active ? '#28a745' : '#6c757d',
                            color: 'white',
                            fontSize: '11px',
                            padding: '4px 8px',
                            cursor: 'pointer',
                            border: 'none'
                          }}
                          onClick={() => handleToggleStatus(banner._id, banner.active)}
                          title={`Click to ${banner.active ? 'deactivate' : 'activate'}\nCreated: ${banner.createdAt ? new Date(banner.createdAt).toLocaleString() : '-'}\nUpdated: ${banner.updatedAt ? new Date(banner.updatedAt).toLocaleString() : '-'}`}
                        >
                          {banner.active ? 'Active' : 'Inactive'}
                        </button>
                        {banner.updatedAt && (
                          <div style={{ fontSize: '11px', color: '#6c757d', marginTop: 4 }}>
                            Updated: {new Date(banner.updatedAt).toLocaleString()}
                          </div>
                        )}
                      </td>
                      <td>
                        <button
                          className="btn btn-sm me-1 py-0 px-2 fs-6 lh-sm shadow-none border-0"
                          style={{ color: '#F94F01' }}
                          title="Edit"
                          onClick={() => handleEdit(banner)}
                        >
                          <FaEdit size={14} />
                        </button>
                        <button
                          className="btn btn-sm py-0 px-2 fs-6 lh-sm shadow-none border-0"
                          style={{ color: 'red' }}
                          title="Delete"
                          onClick={() => handleDelete(banner._id)}
                        >
                          <FaTrash size={14} />
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="9" className="text-center py-2">No Sakhyam Ads found.</td>
                  </tr>
                )}
              </tbody>
            </table>
            {/* Pagination controls removed */}
          </div>
        </div>
      </div>

      {/* Modal for add/edit */}
      <div className={`modal fade ${showModal ? 'show' : ''}`} style={{ display: showModal ? 'block' : 'none' }}>
        <div className="modal-dialog modal-lg">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">{editingId ? 'Edit' : 'Add New'} Sakhyam Ads</h5>
              <button
                type="button"
                className="btn-close"
                onClick={handleCancel}
              ></button>
            </div>
            <div className="modal-body">
              <div className="mb-3">
                <label className="form-label">Image
                  <button
                    type="button"
                    className="btn btn-secondary btn-sm rounded-pill px-2 py-0"
                    data-bs-toggle="tooltip"
                    data-bs-placement="top"
                    data-bs-html="true"
                    title={`You may upload one image. Allowed: jpg, jpeg, png. Max 2MB. Required size: 400x500.px`}
                  >
                    ?
                  </button>
                  </label>
                <div className="d-flex align-items-center gap-2">
                  <input
                    type="file"
                    className="form-control"
                    accept="image/jpeg,image/jpg,image/png"
                    onChange={async e => {
                      const file = e.target.files[0];
                      if (!file) return;
                      try {
                        // Slider required dimensions: 400x500
                        await validateImageFile(file, 400, 500);
                        setFormValues(prev => ({ ...prev, images: file }));
                        setImagePreview(URL.createObjectURL(file));
                        if (errors.images) setErrors(prev => ({ ...prev, images: '' }));
                      } catch (err) {
                        setErrors(prev => ({ ...prev, images: err }));
                        setFormValues(prev => ({ ...prev, images: null }));
                        setImagePreview('');
                        toastError(err);
                      }
                    }}
                  />
                </div>
                {imagePreview && (
                  <div className="mt-2">
                    <img
                      src={imagePreview}
                      alt="Preview"
                      style={{
                        width: '100%',
                        maxHeight: '200px',
                        objectFit: 'cover',
                        borderRadius: '4px',
                        border: '1px solid #ddd'
                      }}
                    />
                  </div>
                )}
                {errors.images && (
                  <div className="text-danger" style={{ fontSize: '12px' }}>
                    {errors.images}
                  </div>
                )}
              </div>
              <div className="mb-3">
                <label className="form-label">Link</label>
                <input
                  type="text"
                  className="form-control"
                  value={formValues.link}
                  onChange={e => setFormValues(prev => ({ ...prev, link: e.target.value }))}
                />
                {errors.link && (
                  <div className="text-danger" style={{ fontSize: '12px' }}>
                    {errors.link}
                  </div>
                )}
              </div>
              <div className="mb-3">
                <label className="form-label">Start Date</label>
                <input
                  type="date"
                  className="form-control"
                  value={formValues.startDate}
                  onChange={e => setFormValues(prev => ({ ...prev, startDate: e.target.value }))}
                />
                {errors.startDate && (
                  <div className="text-danger" style={{ fontSize: '12px' }}>
                    {errors.startDate}
                  </div>
                )}
              </div>
              <div className="mb-3">
                <label className="form-label">End Date</label>
                <input
                  type="date"
                  className="form-control"
                  value={formValues.endDate}
                  onChange={e => setFormValues(prev => ({ ...prev, endDate: e.target.value }))}
                />
                {errors.endDate && (
                  <div className="text-danger" style={{ fontSize: '12px' }}>
                    {errors.endDate}
                  </div>
                )}
              </div>
              <div className="mb-3">
                <label className="form-label">Price</label>
                <input
                  type="text"
                  className="form-control"
                  value={formValues.price}
                  onChange={e => setFormValues(prev => ({ ...prev, price: e.target.value }))}
                />
                {errors.price && (
                  <div className="text-danger" style={{ fontSize: '12px' }}>
                    {errors.price}
                  </div>
                )}
              </div>
              <div className="mb-3">
                <label className="form-label d-block">Active</label>
                <div>
                  <Switch
                    onChange={checked => setFormValues(prev => ({ ...prev, active: checked }))}
                    checked={!!formValues.active}
                    onColor="#28a745"
                    offColor="#6c757d"
                    uncheckedIcon={false}
                    checkedIcon={false}
                    height={20}
                    width={44}
                    handleDiameter={18}
                  />
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={handleCancel}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn"
                style={{ backgroundColor: '#F94F01', color: '#fff' }}
                onClick={handleSave}
                disabled={saving}
              >
                {saving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* View Modal */}
      <div className={`modal fade ${showViewModal ? 'show' : ''}`} style={{ display: showViewModal ? 'block' : 'none' }}>
        <div className="modal-dialog modal-lg">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">View Sakhyam Ads</h5>
              <button
                type="button"
                className="btn-close"
                onClick={() => setShowViewModal(false)}
              ></button>
            </div>
            <div className="modal-body">
              {selectedBanner && (
                <div>
                  <div className="mb-3">
                    <img
                      src={selectedBanner.images}
                      alt="Slider"
                      style={{
                        width: '100%',
                        maxHeight: '400px',
                        objectFit: 'cover',
                        borderRadius: '4px',
                        border: '1px solid #ddd'
                      }}
                    />
                  </div>
                  <div className="mb-3">
                    <label className="fw-bold">Ads Id:</label>
                    <div>{selectedBanner.bannerId || selectedBanner._id}</div>
                  </div>
                  <div className="mb-3">
                    <label className="fw-bold">Link:</label>
                    <div>{selectedBanner.link || '-'}</div>
                  </div>
                  <div className="mb-3">
                    <label className="fw-bold">Start Date:</label>
                    <div>{selectedBanner.startDate ? new Date(selectedBanner.startDate).toLocaleDateString() : '-'}</div>
                  </div>
                  <div className="mb-3">
                    <label className="fw-bold">End Date:</label>
                    <div>{selectedBanner.endDate ? new Date(selectedBanner.endDate).toLocaleDateString() : '-'}</div>
                  </div>
                  <div className="mb-3">
                    <label className="fw-bold">Price:</label>
                    <div>₹{selectedBanner.price || 0}</div>
                  </div>
                  <div className="mb-3">
                    <label className="fw-bold">Status:</label>
                    <div>
                      <span
                        className={`badge ${selectedBanner.active ? 'badge-success' : 'badge-secondary'}`}
                        style={{
                          backgroundColor: selectedBanner.active ? '#28a745' : '#6c757d',
                          color: 'white',
                          fontSize: '11px',
                          padding: '4px 8px',
                        }}
                      >
                        {selectedBanner.active ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                      <div style={{ marginTop: 8 }}>
                        <button
                          type="button"
                          className={`btn btn-sm ${selectedBanner.active ? 'btn-outline-danger' : 'btn-success'}`}
                          onClick={handleViewToggle}
                        >
                          {selectedBanner.active ? 'Deactivate' : 'Activate'}
                        </button>
                      </div>
                  </div>
                  <div className="mb-3">
                    <label className="fw-bold">Created:</label>
                    <div>{selectedBanner.createdAt ? new Date(selectedBanner.createdAt).toLocaleString() : '-'}</div>
                  </div>
                  <div className="mb-3">
                    <label className="fw-bold">Updated:</label>
                    <div>{selectedBanner.updatedAt ? new Date(selectedBanner.updatedAt).toLocaleString() : '-'}</div>
                  </div>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setShowViewModal(false)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Filter Modal */}
      <div className={`modal fade ${showFilterModal ? 'show' : ''}`} style={{ display: showFilterModal ? 'block' : 'none' }}>
        <div className="modal-dialog">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">Filter Sakhyam Ads by Date Range</h5>
              <button
                type="button"
                className="btn-close"
                onClick={() => setShowFilterModal(false)}
              ></button>
            </div>
            <div className="modal-body">
              <div className="mb-3">
                <label className="form-label">Start Date</label>
                <input
                  type="date"
                  className="form-control"
                  value={tempDateRange.start}
                  onChange={e => setTempDateRange(prev => ({ ...prev, start: e.target.value }))}
                />
              </div>
              <div className="mb-3">
                <label className="form-label">End Date</label>
                <input
                  type="date"
                  className="form-control"
                  value={tempDateRange.end}
                  onChange={e => setTempDateRange(prev => ({ ...prev, end: e.target.value }))}
                />
              </div>
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setShowFilterModal(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn"
                style={{ backgroundColor: '#F94F01', color: '#fff' }}
                onClick={handleApplyFilter}
              >
                Apply Filter
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SliderPage;

